import { useState } from 'react';

import { formatMoney, useGraphQLMutation } from '@betterment/js-runtime';
import {
  Banner,
  Box,
  Button,
  FormikField as Field,
  Heading,
  Input,
  PancakeStack,
  Select,
  Separator,
  Sheet,
  Text,
  Tooltip,
} from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { NarrowTypename } from '../../../../../graphql/utils/narrow-typename';
import { urlBuilder } from '../../../../../Shared/urlBuilder';
import { convertToMoneyWithDefault } from '../../../../shared/helpers/utils';
import { ForecasterPurposeSettingsQuery } from '../../../graphql/forecasterPurposeSettings.generated';
import { UpdateFinancialProfileRetirementAssumptionsDocument } from '../../../graphql/mutation.updateFinancialProfileRetirementAssumptions.generated';

type Props = {
  purpose: NarrowTypename<ForecasterPurposeSettingsQuery['purpose'], 'PurposeRetirementGrowth'>;
};

const schema = Yup.object().shape({
  aboveTheLineDeductions: Yup.string().required('Above the line deductions is required'),
  annualSalaryGrowthAfterInflation: Yup.number().required('Annual salary growth after inflation is required'),
  expectedAnnualInflation: Yup.number().required('Expected annual inflation is required'),
  expectedSocialSecurityPayout: Yup.number().required('Expected Social Security is required'),
  itemizedDeductions: Yup.string().required('Itemized deductions is required'),
  householdAdjustedGrossIncome: Yup.string().required('Household adjusted gross income is required'),
  lifeExpectancyAge: Yup.number().required('Life expectancy is required'),
  numberOfDependents: Yup.number().required('Number of dependents is required'),
  otherRetirementIncome: Yup.string().required('Other retirement income is required'),
  socialSecurityBenefit: Yup.string().required('Social security benefit is required'),
  socialSecurityStartAge: Yup.number().required('Social security start age is required'),
  spouseLifeExpectancyAge: Yup.number().optional(),
  spousesocialSecurityStartAge: Yup.number().optional(),
  spouseSocialSecurityBenefit: Yup.string().optional(),
});

export const RetirementGrowthTargetSheet = ({ purpose }: Props) => {
  const [mutationErrorMessage, setMutationErrorMessage] = useState<string | undefined>();
  const [updateFinancialProfileRetirementAssumptions] = useGraphQLMutation(
    UpdateFinancialProfileRetirementAssumptionsDocument
  );

  if (!purpose.financialProfile.retirementAssumptions) return null;

  const {
    financialProfile: {
      retirementAssumptions: {
        annualSalaryGrowthAfterInflation,
        annualInflation,
        socialSecurityPayout,
        annualAdjustedGrossIncomeAmount,
        annualModifiedAdjustedGrossIncomeAmount,
        annualItemizedDeductionsAmount,
        annualAboveTheLineDeductionsAmount,
        userAssumptions: { lifeExpectancyAge, socialSecurityStartAge, socialSecurityAnnualBenefit },
        annualOtherRetirementIncome,
      },
      annualPretaxIncome,
      spouse,
      numberOfDependents,
      federalTaxBracket,
    },
  } = purpose;

  const initialValues = {
    annualSalaryGrowthAfterInflation,
    expectedAnnualInflation: annualInflation,
    expectedSocialSecurityPayout: socialSecurityPayout.toString(),
    householdAdjustedGrossIncome: annualAdjustedGrossIncomeAmount
      ? formatMoney(annualAdjustedGrossIncomeAmount, 'raw')
      : formatMoney(annualPretaxIncome, 'raw'),
    householdModifiedAdjustedGrossIncome: annualModifiedAdjustedGrossIncomeAmount
      ? formatMoney(annualModifiedAdjustedGrossIncomeAmount, 'raw')
      : formatMoney(annualPretaxIncome, 'raw'),
    itemizedDeductions: formatMoney(annualItemizedDeductionsAmount, 'raw'),
    aboveTheLineDeductions: formatMoney(annualAboveTheLineDeductionsAmount, 'raw'),
    lifeExpectancyAge,
    spouseLifeExpectancyAge: spouse?.retirementUserAssumptions?.lifeExpectancyAge,
    numberOfDependents: numberOfDependents ?? 0,
    otherRetirementIncome: formatMoney(annualOtherRetirementIncome, 'raw'),
    socialSecurityBenefit: formatMoney(socialSecurityAnnualBenefit, 'raw'),
    socialSecurityStartAge,
    spousesocialSecurityStartAge: spouse?.retirementUserAssumptions?.socialSecurityStartAge,
    spouseSocialSecurityBenefit: spouse?.retirementUserAssumptions?.socialSecurityAnnualBenefit
      ? formatMoney(spouse.retirementUserAssumptions.socialSecurityAnnualBenefit, 'raw')
      : undefined,
  };

  const handleSubmit = async (values: typeof initialValues) => {
    const { data: mutationResult } = await updateFinancialProfileRetirementAssumptions({
      variables: {
        input: {
          adjustedGrossIncome: convertToMoneyWithDefault(values.householdAdjustedGrossIncome),
          expectedAnnualSalaryGrowthPercent: values.annualSalaryGrowthAfterInflation,
          expectedInflationPercent: values.expectedAnnualInflation,
          expectedSocialSecurityPercent: Number(values.expectedSocialSecurityPayout),
          itemizedDeductions: convertToMoneyWithDefault(values.itemizedDeductions),
          aboveTheLineDeductions: convertToMoneyWithDefault(values.aboveTheLineDeductions),
          lifeExpectancyAge: values.lifeExpectancyAge,
          numberOfDependents: values.numberOfDependents,
          otherRetirementIncome: convertToMoneyWithDefault(values.otherRetirementIncome),
          socialSecurityYearlyBenefit: convertToMoneyWithDefault(values.socialSecurityBenefit),
          socialSecurityStartAge: values.socialSecurityStartAge,
          spouseLifeExpectancyAge: values.spouseLifeExpectancyAge ?? null,
          spouseSocialSecurityStartAge: values.spousesocialSecurityStartAge ?? null,
          spouseSocialSecurityYearlyBenefit:
            values.spouseSocialSecurityBenefit !== undefined
              ? convertToMoneyWithDefault(values.spouseSocialSecurityBenefit)
              : null,
        },
      },
    });

    if (mutationResult) {
      const { displayableErrors } = mutationResult.updateFinancialProfileRetirementAssumptions;

      if (displayableErrors[0]) {
        setMutationErrorMessage(displayableErrors[0].message);
        return;
      }

      setMutationErrorMessage(undefined);
      window.location.reload();
    }
  };

  const spouseSocialSecurityBenefit = spouse?.retirementUserAssumptions?.socialSecurityAnnualBenefit
    ? formatMoney(spouse?.retirementUserAssumptions?.socialSecurityAnnualBenefit)
    : '&mdash;';

  return (
    <Sheet overlay="weak">
      <Box>
        <Sheet.Trigger asChild={true}>
          <Button variant="textPrimary">Edit additional assumptions</Button>
        </Sheet.Trigger>
      </Box>
      <Sheet.Content>
        <PancakeStack css={{ height: '100vh', display: 'flex', flexDirection: 'column', overflowY: 'scroll' }}>
          <PancakeStack.Header css={{ p: 'space.300', pt: 'space.500' }}>
            <Sheet.Title>Retirement assumptions</Sheet.Title>
          </PancakeStack.Header>
          <PancakeStack.Main css={{ px: 'space.300', pb: 'space.300', flex: 1 }}>
            {!!mutationErrorMessage && (
              <Banner variant="critical" css={{ mb: 'space.200' }}>
                <Text>{mutationErrorMessage}</Text>
              </Banner>
            )}
            <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema}>
              {({ isSubmitting }) => (
                <Form>
                  <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.400' }}>
                    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.200' }}>
                      <Heading as="h3" variant="title200">
                        About you
                      </Heading>
                      <Field name="lifeExpectancyAge">
                        <Field.Label>Your life expectancy</Field.Label>
                        <Field.Control>
                          <Input type="number" />
                        </Field.Control>
                        <Field.ErrorMessage />
                      </Field>
                      {spouse && (
                        <Field name="spouseLifeExpectancyAge">
                          <Field.Label>{spouse.name}'s life expectancy</Field.Label>
                          <Field.Control>
                            <Input type="number" />
                          </Field.Control>
                          <Field.ErrorMessage />
                        </Field>
                      )}
                      <Field name="numberOfDependents">
                        <Field.Label>
                          Number of dependents
                          <Tooltip>
                            <Tooltip.Trigger asChild={true}>
                              <Tooltip.IconButton
                                name="InfoCircle"
                                css={{ ml: 'space.50', display: 'inline-block', verticalAlign: 'text-bottom' }}
                              />
                            </Tooltip.Trigger>
                            <Tooltip.Content>
                              Enter the number of dependent exemptions taken on your tax return. If not sure, enter 0.
                              This is used for estimating your current tax bracket.
                            </Tooltip.Content>
                          </Tooltip>
                        </Field.Label>
                        <Field.Control>
                          <Input type="number" placeholder="0" />
                        </Field.Control>
                        <Field.ErrorMessage />
                      </Field>
                    </Box>
                    <Separator />
                    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.200' }}>
                      <Heading as="h3" variant="title200">
                        About your income
                      </Heading>
                      <Field name="annualSalaryGrowthAfterInflation">
                        <Field.Label>Annual salary growth after inflation</Field.Label>
                        <Field.Control>
                          <Input type="number" trailingSymbol="%" />
                        </Field.Control>
                        <Field.ErrorMessage />
                      </Field>
                      <Field name="socialSecurityStartAge">
                        <Field.Label>Social Security collection age</Field.Label>
                        <Field.Control>
                          <Input type="number" placeholder="0" />
                        </Field.Control>
                        <Field.ErrorMessage />
                      </Field>
                      {spouse && (
                        <Field name="spousesocialSecurityStartAge">
                          <Field.Label>{spouse.name}'s Social Security collection age</Field.Label>
                          <Field.Control>
                            <Input type="number" />
                          </Field.Control>
                          <Field.ErrorMessage />
                        </Field>
                      )}
                      <Field name="socialSecurityBenefit">
                        <Field.Label>
                          Your Social Security benefit
                          <Tooltip>
                            <Tooltip.Trigger asChild={true}>
                              <Tooltip.IconButton
                                name="InfoCircle"
                                css={{ ml: 'space.50', display: 'inline-block', verticalAlign: 'text-bottom' }}
                              />
                            </Tooltip.Trigger>
                            <Tooltip.Content>
                              Social Security provides income in Retirement with a Cost of Living Adjustment. Not
                              everyone starts their Social Security Benefits in the year they retire since delaying
                              benefits can increase them. Your benefit is estimated based on your current age and
                              income, but you may input your own estimate.
                            </Tooltip.Content>
                          </Tooltip>
                        </Field.Label>
                        <Field.Control>
                          <Input mask="number" leadingSymbol="$" placeholder="0" />
                        </Field.Control>
                        <Field.ErrorMessage />
                      </Field>
                      {spouse && (
                        <Field name="spouseSocialSecurityBenefit">
                          <Field.Label>
                            {spouse.name}'s Social Security benefit
                            <Tooltip>
                              <Tooltip.Trigger asChild={true}>
                                <Tooltip.IconButton
                                  name="InfoCircle"
                                  css={{ ml: 'space.50', display: 'inline-block', verticalAlign: 'text-bottom' }}
                                />
                              </Tooltip.Trigger>
                              <Tooltip.Content>
                                Social Security provides income in Retirement with a Cost of Living Adjustment. Not
                                everyone starts their Social Security Benefits in the year they retire since delaying
                                benefits can increase them. Your benefit is estimated based on your current age and
                                income, but you may input your own estimate.
                              </Tooltip.Content>
                            </Tooltip>
                          </Field.Label>
                          <Field.Control>
                            <Input mask="number" leadingSymbol="$" placeholder="0" />
                          </Field.Control>
                          <Field.ErrorMessage />
                        </Field>
                      )}
                      <Field name="otherRetirementIncome">
                        <Field.Label>
                          Other retirement income (pre-tax)
                          <Tooltip>
                            <Tooltip.Trigger asChild={true}>
                              <Tooltip.IconButton
                                name="InfoCircle"
                                css={{ ml: 'space.50', display: 'inline-block', verticalAlign: 'text-bottom' }}
                              />
                            </Tooltip.Trigger>
                            <Tooltip.Content>
                              Enter the total amount of annual pre-tax income you expect from sources besides Social
                              Security and your portfolio. This may include pensions, rental income or other sources. We
                              assume this income starts at retirement and continues until life expectancy.
                            </Tooltip.Content>
                          </Tooltip>
                        </Field.Label>
                        <Field.Control>
                          <Input mask="number" leadingSymbol="$" placeholder="0" />
                        </Field.Control>
                        <Field.ErrorMessage />
                      </Field>
                    </Box>
                    <Separator />
                    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.200' }}>
                      <Heading as="h3" variant="title200">
                        About the economy
                      </Heading>
                      <Field name="expectedAnnualInflation">
                        <Field.Label>Expected annual inflation</Field.Label>
                        <Field.Control>
                          <Input type="number" trailingSymbol="%" />
                        </Field.Control>
                        <Field.ErrorMessage />
                      </Field>
                      <Field name="expectedSocialSecurityPayout">
                        <Field.Label>
                          Expected Social Security
                          <Tooltip>
                            <Tooltip.Trigger asChild={true}>
                              <Tooltip.IconButton
                                name="InfoCircle"
                                css={{ ml: 'space.50', display: 'inline-block', verticalAlign: 'text-bottom' }}
                              />
                            </Tooltip.Trigger>
                            <Tooltip.Content>
                              Given the instability of the Social Security system, the Social Security Administration
                              estimates that only three-quarters (3/4) of benefits may be available to younger earners.
                              Adjust your full benefits with this control. We assume your full benefit amount is{' '}
                              {formatMoney(socialSecurityAnnualBenefit)}
                              /year starting at {socialSecurityStartAge}.{' '}
                              {spouse
                                ? `${spouse.name}'s full benefit amount is ${spouseSocialSecurityBenefit}/year starting at ${socialSecurityStartAge}. `
                                : ''}
                              You can change your benefit amount and start age above.
                            </Tooltip.Content>
                          </Tooltip>
                        </Field.Label>
                        <Field.Control>
                          <Select>
                            <Select.Trigger appearance="default">
                              <Select.Value placeholder="Select a Social Security payout" />
                            </Select.Trigger>
                            <Select.Content>
                              <Select.Item value="0">None</Select.Item>
                              <Select.Item value="25">1/4</Select.Item>
                              <Select.Item value="50">1/2</Select.Item>
                              <Select.Item value="75">3/4</Select.Item>
                              <Select.Item value="100">Full</Select.Item>
                            </Select.Content>
                          </Select>
                        </Field.Control>
                        <Field.ErrorMessage />
                      </Field>
                    </Box>
                    <Separator />
                    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.200' }}>
                      <Heading as="h3" variant="title200">
                        About your taxes
                      </Heading>
                      <Box css={{ display: 'grid', backgroundColor: 'background', padding: 'space.300' }}>
                        <Box
                          css={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 'space.200',
                          }}
                        >
                          <Text css={{ textTransform: 'uppercase' }} variant="text25" color="text.weak">
                            FROM YOUR PROFILE
                          </Text>
                          <SambaAutoLink
                            to={urlBuilder.appFinancialProfileEdit.absolute({ return_to: window.location.pathname })}
                            variant="textPrimary"
                            css={{ fontSize: 'fontSize.50' }}
                          >
                            Edit profile
                          </SambaAutoLink>
                        </Box>
                        <Text variant="text50">Your pre-tax income: {formatMoney(annualPretaxIncome)}/year</Text>
                        {federalTaxBracket && <Text variant="text50">Federal tax bracket: {federalTaxBracket}%</Text>}
                      </Box>
                      <Field name="itemizedDeductions">
                        <Field.Label>
                          <Text variant="text50">Mortgage interest & allowable real estate tax</Text>
                          <Tooltip>
                            <Tooltip.Trigger asChild={true}>
                              <Tooltip.IconButton
                                name="InfoCircle"
                                css={{ ml: 'space.50', display: 'inline-block', verticalAlign: 'text-bottom' }}
                              />
                            </Tooltip.Trigger>
                            <Tooltip.Content>
                              If you take the standard deduction{' '}
                              <SambaAutoLink
                                to="https://taxfoundation.org/90-percent-taxpayers-projected-tcja-expanded-standard-deduction/"
                                target="_blank"
                              >
                                as do approximately 90% of taxpayers
                              </SambaAutoLink>{' '}
                              or aren't sure, leave this field 0. If you itemize deductions on page 2 of your Form 1040
                              (Schedule A) to deduct mortgage interest or other items, enter the total deductions
                              excluding the state tax paid, which we estimate for you. This is used for estimating your
                              current tax bracket.
                            </Tooltip.Content>
                          </Tooltip>
                        </Field.Label>
                        <Field.Control>
                          <Input mask="number" leadingSymbol="$" placeholder="0" />
                        </Field.Control>
                        <Field.ErrorMessage />
                      </Field>
                      <Field name="aboveTheLineDeductions">
                        <Field.Label>
                          Deductions for student loans, tuition, etc
                          <Tooltip>
                            <Tooltip.Trigger asChild={true}>
                              <Tooltip.IconButton
                                name="InfoCircle"
                                css={{ ml: 'space.50', display: 'inline-block', verticalAlign: 'text-bottom' }}
                              />
                            </Tooltip.Trigger>
                            <Tooltip.Content>
                              Per Worksheet 1-1 in IRS Pub 590a, add up the deductions you took for student loan
                              interest, tuition and fees, and any other items that apply to you.
                            </Tooltip.Content>
                          </Tooltip>
                        </Field.Label>
                        <Field.Control>
                          <Input mask="number" leadingSymbol="$" placeholder="0" />
                        </Field.Control>
                        <Field.ErrorMessage />
                      </Field>
                      <Field name="householdAdjustedGrossIncome">
                        <Field.Label>
                          Household adjusted gross income
                          <Tooltip>
                            <Tooltip.Trigger asChild={true}>
                              <Tooltip.IconButton
                                name="InfoCircle"
                                css={{ ml: 'space.50', display: 'inline-block', verticalAlign: 'text-bottom' }}
                              />
                            </Tooltip.Trigger>
                            <Tooltip.Content>
                              Adjusted gross income (AGI) is the last number on the first page of your personal tax
                              return Form 1040, 1040A or 1040NR. If you made traditional IRA contributions for you or
                              your spouse, exclude the deducted amount from this number.
                            </Tooltip.Content>
                          </Tooltip>
                        </Field.Label>
                        <Field.Control>
                          <Input mask="number" leadingSymbol="$" placeholder="0" />
                        </Field.Control>
                        <Field.ErrorMessage />
                      </Field>
                      <Field name="householdModifiedAdjustedGrossIncome">
                        <Field.Label>
                          Household modified adjusted gross income
                          <Tooltip>
                            <Tooltip.Trigger asChild={true}>
                              <Tooltip.IconButton
                                name="InfoCircle"
                                css={{ ml: 'space.50', display: 'inline-block', verticalAlign: 'text-bottom' }}
                              />
                            </Tooltip.Trigger>
                            <Tooltip.Content>
                              <SambaAutoLink to={urlBuilder.getGoalMAGIIncomeCalculationHelp()} target="_blank">
                                Modified adjusted gross income
                              </SambaAutoLink>{' '}
                              is used to generate your How to Save recommendations but is not editable.
                            </Tooltip.Content>
                          </Tooltip>
                        </Field.Label>
                        <Field.Control>
                          <Input mask="number" leadingSymbol="$" placeholder="0" disabled={true} />
                        </Field.Control>
                        <Field.Description>
                          <Text color="text.weak" variant="text50">
                            Calculated
                          </Text>
                        </Field.Description>
                        <Field.ErrorMessage />
                      </Field>
                    </Box>
                    <Button type="submit" disabled={isSubmitting}>
                      Save changes
                    </Button>
                  </Box>
                </Form>
              )}
            </Formik>
          </PancakeStack.Main>
        </PancakeStack>
      </Sheet.Content>
    </Sheet>
  );
};
